import React, { useEffect, useRef, useState } from "react";
import API from "../lib/TimeEditAPI";
import {
    MillenniumDate,
    MillenniumDateTime,
    MillenniumTime,
    SimpleDateFormat,
} from "@timeedit/millennium-time";
import TemplateKind from "../models/TemplateKind";
import ConflictListSettings from "./ConflictListSettings";
import Language from "../lib/Language";
import { Header } from "../models/Header";
const Size = Header.Label;
import _ from "underscore";
import GenericList from "./GenericList";
import { TimeConstants as TC } from "../lib/TimeConstants";
import { TTypeNameClass } from "../types/api/APIClassTypes";
import { IGetTemplateGroupsResponse } from "../types/api/APIResponseTypes";
import { AppContextProvider, useAppContext } from "../contexts/AppContext";
import { ConflictSearch } from "../models/ConflictSearch";
import { Limits } from "../models/Limits";
import {
    TListCalendarInjectedProps,
    TListCalendarWrapperProps,
} from "../types/components/ListCalendarWrapper";
import Log from "../lib/Log";
import { TComponent } from "../types/utils";
import { TListFind } from "../types/components/GenericList";
import { TObject } from "../types/api/shared";

const SETTINGS_TO_SAVE = [
    "beginTime",
    "endTime",
    "selectedType",
    "selectedReservationMode",
    "includeUnintentionalDoubleReservations",
    "includeIntentionalDoubleReservations",
    "includeCollisionsOutsideOfFilters",
    "objects",
    "filterObjects",
    "objectCategories",
    "doubleReservationState",
    "columnWidths",
    "selectedColumns",
    "selectedInfoColumns",
];
/*
doubleReservationObject: result[0][i],
                    firstReservation: result[1][i],
                    firstObject: result[2][i],
                    secondReservation: result[3][i],
                    secondObject: result[4][i],
                    */

// Only 90 days are allowed
const DURATION_LIMIT = 91;

const LABEL_SIZE_CUTOFFS = {
    MINIMUM: 10,
    XS: 30,
    S: 40,
    M: 60,
    L: 110,
};

const COL_IDS = {
    DR_OBJECT: 1,
    FIRST_RES: 2,
    FIRST_OBJECT: 3,
    SECOND_RES: 4,
    SECOND_OBJECT: 5,
    DATE: 6,
    TAGS_FIRST_RES: 7,
    TAGS_SECOND_RES: 8,
};

const getAvailableColumns = () => [
    {
        name: Language.get("nc_double_reservation_object"),
        primary: true,
        sortable: false,
        id: COL_IDS.DR_OBJECT,
        defaultWidth: 100,
    },
    {
        name: Language.get("nc_conflict_first_reservation"),
        primary: true,
        sortable: true,
        id: COL_IDS.FIRST_RES,
        defaultWidth: 100,
    },
    {
        name: Language.get("nc_conflict_list_tags_first_reservation"),
        primary: false,
        sortable: false,
        id: COL_IDS.TAGS_FIRST_RES,
    },
    {
        name: Language.get("nc_conflict_first_object"),
        primary: true,
        sortable: false,
        id: COL_IDS.FIRST_OBJECT,
        defaultWidth: 100,
    },
    {
        name: Language.get("nc_conflict_second_reservation"),
        primary: true,
        sortable: false,
        id: COL_IDS.SECOND_RES,
    },
    {
        name: Language.get("nc_conflict_list_tags_second_reservation"),
        primary: false,
        sortable: false,
        id: COL_IDS.TAGS_SECOND_RES,
    },
    {
        name: Language.get("nc_conflict_second_object"),
        primary: false,
        sortable: false,
        id: COL_IDS.SECOND_OBJECT,
    },
    {
        name: Language.get("cal_reservation_list_column_date"),
        primary: false,
        sortable: false,
        id: COL_IDS.DATE,
    },
    /*{ // To present time, probably need to figure out a useful and clear way to present the different times of the reservations
        name: Language.get("cal_reservation_list_column_time"),
        primary: false,
        sortable: false,
        id: 7
    },*/
];

type TSearchOptions = {
    beginTime: number;
    endTime: number;
    selectedType: number;
    selectedReservationMode: number;
    objects: any[];
    filterObjects: any[];
    objectCategories: {};
    // Set through onSearchSettingsChange by ConflictListSettings and read in
    // find(); they were used here without ever being declared.
    includeUnintentionalDoubleReservations?: boolean;
    includeIntentionalDoubleReservations?: boolean;
    includeCollisionsOutsideOfFilters?: boolean;
};

type TConflictListProps = TListCalendarWrapperProps<ConflictSearch> & TListCalendarInjectedProps;

type TConflictListState = {
    searchOptions: TSearchOptions;
    types: TTypeNameClass[];
    reservationModes: IGetTemplateGroupsResponse["parameters"][0];
    conflictRunning: boolean;
    availableObjectCategories: any[];
    availableObjectCheckboxes: any[];
};

/**
 * getReservationConflicts is untyped, so these describe what the list reads off
 * a conflict rather than everything the server sends.
 */
type TConflictReservation = {
    id: number;
    begin?: number;
    end?: number;
    tags?: string[];
};

/**
 * One row: the object that is double booked, and the two reservations that
 * collide on it with the object each of them matched.
 */
type TConflictRow = {
    doubleReservationObject: Pick<TObject, "fields">;
    firstReservation: TConflictReservation;
    firstObject: Pick<TObject, "fields">;
    secondReservation: TConflictReservation;
    secondObject: Pick<TObject, "fields">;
};

/**
 * Exported so ConflictListSettings can type its onChange without reaching into a
 * component's shape, which is what it did while this was a class
 * (ConflictList["onSearchSettingsChange"]).
 */
// eslint-disable-next-line import/no-unused-modules
export type TConflictSearchSettingsChange = (settings: any, cb?: () => void) => void;

const DEFAULT_TIME_LIMITS = () => ({
    begin: MillenniumDateTime.fromDate(MillenniumDate.today()).getMts(),
    end: 0,
});

// `data` arrives via ListCalendar's cloneElement, so it is absent when the child
// is rendered directly. The class relied on the try/catch swallowing the
// resulting TypeError; the guard makes that explicit.
const getTimeLimits = (data?: { limits?: Limits }) => {
    if (!data?.limits) {
        return DEFAULT_TIME_LIMITS();
    }
    try {
        return {
            begin: MillenniumDateTime.fromDate(data.limits.getStartDate()).getMts(),
            end: MillenniumDateTime.fromDate(data.limits.getEndDate()).getMts(),
        };
    } catch (error) {
        return DEFAULT_TIME_LIMITS();
    }
};

const getDefaultSearchOptions = (timeLimit): TSearchOptions => ({
    beginTime: timeLimit.begin,
    endTime: timeLimit.end,
    selectedType: 0,
    selectedReservationMode: 0,
    objects: [],
    filterObjects: [],
    objectCategories: {},
});

const limitTo = (categories, allowedCategories) =>
    _.omit(
        categories,
        (value, category) =>
            !_.some(
                allowedCategories,
                (allowedCategory) =>
                    String(allowedCategory.id) === category &&
                    _.every(value, (val) => _.contains(allowedCategory.categories, val))
            )
    );

const ConflictList: TComponent<TConflictListProps> = (props) => {
    const appContext = useAppContext();

    // One state object: componentDidMount passed this.setState straight to
    // API.getObjectCategories, relying on the class's partial merge.
    const [state, setState] = useState<TConflictListState>(() => ({
        searchOptions: getDefaultSearchOptions(getTimeLimits(props.data)),
        types: [],
        reservationModes: [],
        conflictRunning: false,
        availableObjectCategories: [],
        availableObjectCheckboxes: [],
    }));

    useEffect(() => {
        API.getObjectCategories((result) => setState((prev) => ({ ...prev, ...result })));
        API.findAliasTypes((types) => {
            API.getTemplateGroups(TemplateKind.RESERVATION, (reservationModes) => {
                setState((prev) => ({
                    ...prev,
                    types,
                    reservationModes: reservationModes.parameters[0],
                }));
            });
        });
    }, []);

    // Re-syncs the time window when data.limits changes identity. This is the one
    // copy of this block that compared timeLimits.end correctly rather than the
    // non-existent timeLimits.endTime.
    const prevLimits = useRef(props.data?.limits);
    useEffect(() => {
        if (prevLimits.current === props.data?.limits) {
            return;
        }
        prevLimits.current = props.data?.limits;
        const timeLimits = getTimeLimits(props.data);
        setState((prev) => {
            if (
                timeLimits.begin === prev.searchOptions.beginTime &&
                timeLimits.end === prev.searchOptions.endTime
            ) {
                return prev;
            }
            return {
                ...prev,
                searchOptions: _.extend({}, prev.searchOptions, {
                    beginTime: timeLimits.begin,
                    endTime: timeLimits.end,
                }),
            };
        });
    });

    // No current caller supplies `cb`; kept for signature compatibility.
    const onSearchSettingsChange: TConflictSearchSettingsChange = (settings, cb) => {
        setState((prev) => ({
            ...prev,
            searchOptions: _.extend(
                {},
                prev.searchOptions,
                _.omit(settings, (value) => value === undefined)
            ),
        }));
        if (cb) {
            cb();
        }
    };

    const getDataMenuItems = (selectedOrderIds, getDataAtIndex, callback, onMassReplace) => {
        const items: any[] = [];

        if (selectedOrderIds.length === 0) {
            items.push({
                label: Language.get("nc_no_order_selected"),
                isDisabled: true,
                emphasize: true,
                action: _.noop,
            });
        }

        items.push([
            {
                label: Language.get("menu_view_info"),
                isDisabled: selectedOrderIds.length === 0,
                action: () => {
                    props.onInfoOpen?.(selectedOrderIds, true);
                },
                classNames: ["icon", "showInfo"],
            },
        ]);

        if (props.getDataMenuItems) {
            items.push(
                props.getDataMenuItems(selectedOrderIds, getDataAtIndex, callback, onMassReplace)
            );
        }
        return items;
    };

    /**
     * GenericList's data source. The first five arguments describe paging, the
     * object search and the sort, none of which the conflict search has: its
     * criteria come from the settings panel and it returns every row at once, so
     * only the callback is used. See TListFind for what each one carries.
     */
    /**
     * Runs the conflict search. The list asks for it through find, with a
     * callback for the rows it should show; the settings panel's Run report
     * button asks for it with none.
     */
    const runConflictSearch = (
        cb?: (
            rows: TConflictRow[],
            totalNumberOfRows: number,
            searchStart: null,
            searchEnd: null
        ) => void
    ) => {
        const query = {
            typeId: state.searchOptions.selectedType,
            objectIds: state.searchOptions.objects.map((obj) => obj.id),
            start: new MillenniumDateTime(state.searchOptions.beginTime).getMts(),
            end: new MillenniumDateTime(state.searchOptions.endTime).getMts(),
            includeUnintentionalDoubleReservations:
                state.searchOptions.includeUnintentionalDoubleReservations,
            includeIntentionalDoubleReservations:
                state.searchOptions.includeIntentionalDoubleReservations,
            filterObjectIds: state.searchOptions.filterObjects.map((obj) => obj.id),
            filterReservationModes: state.searchOptions.selectedReservationMode
                ? [state.searchOptions.selectedReservationMode]
                : [],
            includeCollisionsOutsideOfFilters:
                state.searchOptions.includeCollisionsOutsideOfFilters || false,
            filterCategories: state.searchOptions.objectCategories || [], // fieldValue
        };
        setState((prev) => ({ ...prev, conflictRunning: true }));
        API.getReservationConflicts(query, (result) => {
            const mappedResult: TConflictRow[] = [];
            for (let i = 0; i < result[0].length; i++) {
                // doubleReservationObjects
                // firstReservations
                // firstObjects
                // secondReservations
                // secondObjects
                // status object
                mappedResult.push({
                    doubleReservationObject: result[0][i],
                    firstReservation: result[1][i],
                    firstObject: result[2][i],
                    secondReservation: result[3][i],
                    secondObject: result[4][i],
                });
            }
            const status = result[5];
            if (status.details) {
                Log.error(status.details);
            }
            cb?.(mappedResult, mappedResult.length, null, null);
            setState((prev) => ({ ...prev, conflictRunning: false }));
        });
    };

    const find: TListFind<TConflictRow> = (
        _startRow,
        _objects,
        _columns,
        _sortOrder,
        _sortColumn,
        cb
    ) => runConflictSearch(cb);

    const onSelection = (conflict: TConflictRow) => {
        props.onSelection?.(conflict.firstReservation, conflict.secondReservation);
    };

    const getCellLabels = (order, columns, columnWidths) => {
        const labels = columns.map((column, i) => {
            if (!order) {
                return "";
            }
            if (column.name === Language.get("nc_double_reservation_object")) {
                return order.doubleReservationObject.fields[0].values.join(", ");
            }
            if (column.name === Language.get("nc_conflict_first_reservation")) {
                return String(order.firstReservation.id);
            }
            if (column.name === Language.get("nc_conflict_first_object")) {
                return order.firstObject.fields[0].values.join(", ");
            }
            if (column.name === Language.get("nc_conflict_second_reservation")) {
                return String(order.secondReservation.id);
            }
            if (column.name === Language.get("nc_conflict_second_object")) {
                return order.secondObject.fields[0].values.join(", ");
            }
            if (column.name === Language.get("nc_conflict_list_tags_first_reservation")) {
                return order.firstReservation.tags?.join(", ");
            }
            if (column.name === Language.get("nc_conflict_list_tags_second_reservation")) {
                return order.secondReservation.tags?.join(", ");
            }
            const width = columnWidths[i];
            let format;
            const reservation = order.firstReservation;
            const startTime = reservation.begin ? new MillenniumDateTime(reservation.begin) : null;
            const endTime = reservation.end ? new MillenniumDateTime(reservation.end) : null;
            const between = startTime && endTime ? startTime.daysBetween(endTime) : null;
            const isSameDay = between === 0 || (between === 1 && endTime?.isMidnight());
            if (column.name === Language.get("cal_reservation_list_column_date") && startTime) {
                if (!isSameDay) {
                    // Different start and end date
                    format = Language.getDateFormat("date_f_m_d");
                    const start = SimpleDateFormat.format(startTime, format);
                    const end = SimpleDateFormat.format(
                        endTime!,
                        Language.getDateFormat("date_f_m_d_end")
                    );
                    if (start !== end) {
                        return `${start}-${end}`;
                    }
                    return start;
                }
                format = Language.getDateFormat("date_f_yy_mm_dd");
                return SimpleDateFormat.format(startTime, format);
            }
            if (
                column.name === Language.get("cal_reservation_list_column_time") &&
                reservation.begin
            ) {
                return `${getTime(startTime, width, false)}-${getTime(endTime, width, true)}`;
            }
            if (column.name === Language.get("cal_reservation_list_column_length")) {
                const length = reservation.length
                    ? reservation.length
                    : reservation.end - reservation.begin;
                if (length === 0 || isNaN(length)) {
                    return "0:00";
                }
                const hours = Math.floor(length / TC.SECONDS_PER_HOUR);
                const minutes = Math.floor((length % TC.SECONDS_PER_HOUR) / TC.SECONDS_PER_MINUTE);
                // eslint-disable-next-line no-magic-numbers
                return `${hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
            }
            return "";
        });

        return { labels };
    };

    const getTime = (time, factor, size) => {
        if (factor > 1) {
            // eslint-disable-next-line no-param-reassign
            time = Math.floor(time / factor);
            return Math.floor(time / TC.SECONDS_PER_MINUTE);
        }
        // eslint-disable-next-line no-param-reassign
        time = new MillenniumTime(time);
        switch (getLabelSize(size)) {
            case Size.M:
                return SimpleDateFormat.format(time, Language.getDateFormat("date_f_hh"));
            default:
                return SimpleDateFormat.format(time, Language.getDateFormat("date_f_hh_mm"));
        }
    };

    const getLongDate = (date, size) => {
        switch (getLabelSize(size)) {
            case Size.XS:
                return SimpleDateFormat.format(date, Language.getDateFormat("date_f_d"));
            case Size.S:
                return SimpleDateFormat.format(date, Language.getDateFormat("date_f_d"));
            case Size.M:
                return SimpleDateFormat.format(date, Language.getDateFormat("date_f_m_d"));
            case Size.L:
                return SimpleDateFormat.format(date, Language.getDateFormat("date_f_ee_m_d"));
            default:
                return SimpleDateFormat.format(
                    date,
                    Language.getDateFormat("date_f_yyyy_mm_dd_hh_mm")
                );
        }
    };

    const getLabelSize = (size) => {
        if (size <= LABEL_SIZE_CUTOFFS.MINIMUM) {
            return null;
        }
        if (size < LABEL_SIZE_CUTOFFS.XS) {
            return Size.XS;
        }
        if (size < LABEL_SIZE_CUTOFFS.S) {
            return Size.S;
        }
        if (size < LABEL_SIZE_CUTOFFS.M) {
            return Size.M;
        }
        if (size < LABEL_SIZE_CUTOFFS.L) {
            return Size.L;
        }
        return Size.XL;
    };

    const isSearchButtonActive = () => {
        const start = new MillenniumDateTime(state.searchOptions.beginTime);
        const end = new MillenniumDateTime(state.searchOptions.endTime);
        return (
            !state.conflictRunning &&
            state.searchOptions.selectedType > 0 &&
            MillenniumDateTime.lengthInDays(start, end) < DURATION_LIMIT
        );
    };

    const loadSettings = (callback, getSavedSettings) => {
        const defaultSettings = getDefaultSearchOptions(getTimeLimits(props.data));
        getSavedSettings((result) => {
            result.forEach((r) => {
                // eslint-disable-next-line no-param-reassign
                r.settings = _.extend({}, defaultSettings, r.settings);
            });
            callback(result);
        });
    };

    const getSettingsComponent = (
        saveDefaultSettings,
        saveSettings,
        getSavedSettings,
        removeSettings
    ) => {
        const opts = state.searchOptions;

        return (
            <AppContextProvider context={appContext}>
                <ConflictListSettings
                    onChange={onSearchSettingsChange}
                    saveDefaultSettings={saveDefaultSettings}
                    saveSettings={saveSettings}
                    getSavedSettings={(cb) => loadSettings(cb, getSavedSettings)}
                    removeSettings={removeSettings}
                    beginTime={state.searchOptions.beginTime}
                    endTime={state.searchOptions.endTime}
                    selectedType={state.searchOptions.selectedType}
                    selectedReservationMode={state.searchOptions.selectedReservationMode}
                    objects={state.searchOptions.objects}
                    filterObjects={state.searchOptions.filterObjects}
                    objectCategories={limitTo(
                        opts.objectCategories,
                        state.availableObjectCategories.concat(state.availableObjectCheckboxes)
                    )}
                    availableObjectCategories={state.availableObjectCategories}
                    availableObjectCheckboxes={state.availableObjectCheckboxes}
                    runReport={() => runConflictSearch()}
                    conflictRunning={state.conflictRunning}
                    types={state.types}
                    reservationModes={state.reservationModes}
                    includeUnintentionalDoubleReservations={
                        state.searchOptions.includeUnintentionalDoubleReservations
                    }
                    includeIntentionalDoubleReservations={
                        state.searchOptions.includeIntentionalDoubleReservations
                    }
                    includeCollisionsOutsideOfFilters={
                        state.searchOptions.includeCollisionsOutsideOfFilters
                    }
                    user={appContext.user}
                />
            </AppContextProvider>
        );
    };

    const classes = {
        conflictList: true, // Was orderList
    };

    return (
        <GenericList
            {...props}
            settingsToSave={SETTINGS_TO_SAVE}
            onSearchSettingsChange={onSearchSettingsChange}
            showSelectionSummary={false}
            allowMultiSelection={true}
            searchOptions={state.searchOptions}
            onSelection={onSelection}
            getCellLabels={getCellLabels}
            columns={getAvailableColumns()}
            defaultSelectedColumns={[
                COL_IDS.DR_OBJECT,
                COL_IDS.FIRST_RES,
                COL_IDS.FIRST_OBJECT,
                COL_IDS.SECOND_RES,
                COL_IDS.SECOND_OBJECT,
            ]}
            getDataMenuItems={getDataMenuItems}
            title={Language.get("nc_conflict_list")}
            classes={classes}
            find={find}
            getSettingsComponent={getSettingsComponent}
            supportsSearch={false}
            manualSearchButton={true}
            isSearchButtonActive={isSearchButtonActive()}
            showLoadIndicator={state.conflictRunning}
        />
    );
};

export default ConflictList;
